import {
  idempotencyTtlMsFor,
  isHighRiskMutationPath,
  messageFromApiError,
  mutationFingerprint,
} from '@lighthouse/shared';

export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) || '/api/v1';

/** Build an absolute API URL for admin fetch calls (path starts with `/`, e.g. `/auth/login`). */
export function adminApiUrl(path: string): string {
  return `${API_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Coerce API list responses — avoids render crashes when an error object is assigned to state. */
export function ensureArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

let accessToken: string | null = null;
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const recentMutationKeys = new Map<string, { key: string; expiresAt: number }>();
const INTENT_STORAGE_PREFIX = 'lh-admin-idempotency:';

export function createAdminIdempotencyKey(): string {
  return `lh-admin-${crypto.randomUUID()}`;
}

function readPersistedKey(fingerprint: string): string | null {
  try {
    const raw = sessionStorage.getItem(`${INTENT_STORAGE_PREFIX}${fingerprint}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { key?: string; expiresAt?: number };
    if (!parsed.key || !parsed.expiresAt || parsed.expiresAt <= Date.now()) {
      sessionStorage.removeItem(`${INTENT_STORAGE_PREFIX}${fingerprint}`);
      return null;
    }
    return parsed.key;
  } catch {
    return null;
  }
}

function persistKey(fingerprint: string, key: string, expiresAt: number): void {
  try {
    sessionStorage.setItem(
      `${INTENT_STORAGE_PREFIX}${fingerprint}`,
      JSON.stringify({ key, expiresAt })
    );
  } catch {
    // ignore
  }
}

function idempotencyKeyFor(endpoint: string, method: string, body: BodyInit | null | undefined) {
  if (typeof body !== 'string') return createAdminIdempotencyKey();
  const fingerprint = mutationFingerprint(method, endpoint, body);
  const ttlMs = idempotencyTtlMsFor(endpoint);
  const now = Date.now();

  const existing = recentMutationKeys.get(fingerprint);
  if (existing && existing.expiresAt > now) return existing.key;

  if (isHighRiskMutationPath(endpoint)) {
    const persisted = readPersistedKey(fingerprint);
    if (persisted) {
      recentMutationKeys.set(fingerprint, { key: persisted, expiresAt: now + ttlMs });
      return persisted;
    }
  }

  const key = createAdminIdempotencyKey();
  const expiresAt = now + ttlMs;
  recentMutationKeys.set(fingerprint, { key, expiresAt });
  if (isHighRiskMutationPath(endpoint)) {
    persistKey(fingerprint, key, expiresAt);
  } else {
    setTimeout(() => {
      if (recentMutationKeys.get(fingerprint)?.key === key) recentMutationKeys.delete(fingerprint);
    }, ttlMs);
  }
  return key;
}

async function waitForIdempotentResponse(request: () => Promise<Response>): Promise<Response> {
  let response = await request();
  for (let attempt = 0; attempt < 3 && response.status === 409; attempt++) {
    if (!response.headers.has('Retry-After')) break;
    await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
    response = await request();
  }
  return response;
}

export function setAdminAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAdminAccessToken(): string | null {
  return accessToken;
}

function accessTokenRole(token: string): string | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      role?: unknown;
    };
    return typeof json.role === 'string' ? json.role : null;
  } catch {
    return null;
  }
}

export async function refreshAdminToken(): Promise<string | null> {
  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'X-Client-Type': 'admin' },
      credentials: 'include',
    });
    if (!response.ok) {
      accessToken = null;
      return null;
    }
    const data = (await response.json()) as { tokens: { accessToken: string } };
    if (accessTokenRole(data.tokens.accessToken) !== 'admin') {
      accessToken = null;
      return null;
    }
    accessToken = data.tokens.accessToken;
    return accessToken;
  } catch {
    accessToken = null;
    return null;
  }
}

export async function adminApi<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Client-Type': 'admin',
    ...(options.headers as Record<string, string>),
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const method = (options.method ?? 'GET').toUpperCase();
  if (MUTATING_METHODS.has(method) && !headers['Idempotency-Key']) {
    headers['Idempotency-Key'] = idempotencyKeyFor(endpoint, method, options.body);
  }

  const send = () =>
    fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });
  let response = await waitForIdempotentResponse(send);

  if (response.status === 401 && accessToken) {
    const newToken = await refreshAdminToken();
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`;
      response = await waitForIdempotentResponse(send);
    }
  }

  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as {
      message?: string;
      details?: { path?: string; message?: string }[];
    } | null;
    throw new Error(messageFromApiError(error, response.status));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export function adminAuthHeaders(): Record<string, string> {
  const token = getAdminAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function adminLogout(): Promise<void> {
  try {
    await adminApi('/auth/logout', { method: 'POST' });
  } catch {
    // proceed with local logout
  }
  accessToken = null;
}
